import { FC, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Dialog, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { QcsButton } from '../../common/basic/QcsButton';
import { TileScope } from '@api/models/dashboard/TileScope';
import { DashboardChartType } from '@api/models/enums/DashboardChartType';
import {
  ConfigChip,
  ConfigChipLabel,
  ConfigChipValue,
  ConfigPreviewGrid,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  TileDialogBackdrop,
  TileDialogPaper,
} from './TileDialogStyles';
import { TileTargetSelector } from './TileTargetSelector';
import { isTotalsMetricKey, TOTALS_METRIC_ORDER, TotalsMetricConfig, TotalsMetricKey } from '../analyticsTotalsConfig';

const TILE_TITLE_MAX_LENGTH = 255;

export interface TileDialogConfig {
  domain: string;
  groupBy: string;
  chartType: DashboardChartType;
  /** Raw metric key (e.g. yKey or totals key) — passed back to onConfirm. */
  metric: string;
  /** Translated metric label shown in the read-only preview chip for chart tiles. */
  metricLabel: string;
  /** Per-domain totals metric labels + formatting; drives the KPI metric picker. */
  totalsMetrics: Record<TotalsMetricKey, TotalsMetricConfig>;
}

const CHART_TYPE_LABEL_KEYS: Partial<Record<DashboardChartType, string>> = {
  [DashboardChartType.Kpi]: 'analytics.chart.kind.kpi',
  [DashboardChartType.Bar]: 'analytics.chart.kind.bar',
  [DashboardChartType.Pie]: 'analytics.chart.kind.pie',
};

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialTitle?: string;
  config: TileDialogConfig;
  availableChartTypes: readonly DashboardChartType[];
  saving?: boolean;
  showTargetSelector?: boolean;
  defaultScope?: TileScope;
  onClose: () => void;
  onConfirm: (title: string, scope: TileScope | undefined, overrides: { chartType: DashboardChartType; metric: string }) => void;
}

const DEFAULT_KPI_METRIC: TotalsMetricKey = 'count';

export const TileDialog: FC<Props> = ({
  open,
  mode,
  initialTitle = '',
  config,
  availableChartTypes,
  saving,
  showTargetSelector,
  defaultScope = 'company',
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle);
  const [error, setError] = useState(false);
  const [scope, setScope] = useState<TileScope>(defaultScope);
  const [chartType, setChartType] = useState<DashboardChartType>(config.chartType);
  const [kpiMetric, setKpiMetric] = useState<TotalsMetricKey>(isTotalsMetricKey(config.metric) ? config.metric : DEFAULT_KPI_METRIC);

  const handleEnter = () => {
    setTitle(initialTitle);
    setError(false);
    setScope(defaultScope);
    setChartType(config.chartType);
    setKpiMetric(isTotalsMetricKey(config.metric) ? config.metric : DEFAULT_KPI_METRIC);
  };

  const handleConfirm = () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError(true);
      return;
    }
    const metric = chartType === DashboardChartType.Kpi ? kpiMetric : config.metric;
    onConfirm(trimmed, showTargetSelector ? scope : undefined, { chartType, metric });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !saving) {
      e.preventDefault();
      handleConfirm();
    }
  };

  const isKpi = chartType === DashboardChartType.Kpi;

  const previewChips: { labelKey: string; value: string }[] = isKpi
    ? []
    : [
        { labelKey: 'analytics.tile.configGroupBy', value: config.groupBy },
        { labelKey: 'analytics.tile.configMetric', value: config.metricLabel },
      ];

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      disableEscapeKeyDown={!!saving}
      aria-busy={!!saving}
      slotProps={{ paper: TileDialogPaper, backdrop: TileDialogBackdrop, transition: { onEnter: handleEnter } }}
    >
      <DialogHeader>
        <DialogTitle>{t(mode === 'create' ? 'analytics.tile.createTitle' : 'analytics.tile.editTitle')}</DialogTitle>
        <DialogSubtitle>{t(mode === 'create' ? 'analytics.tile.createSubtitle' : 'analytics.tile.editSubtitle')}</DialogSubtitle>
      </DialogHeader>

      <DialogBody>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={t('analytics.tile.titleLabel')}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (error) setError(false);
          }}
          onKeyDown={handleKeyDown}
          error={error}
          helperText={error ? t('analytics.tile.titleRequired') : undefined}
          disabled={saving}
          slotProps={{ htmlInput: { maxLength: TILE_TITLE_MAX_LENGTH } }}
        />

        {showTargetSelector && <TileTargetSelector value={scope} onChange={setScope} disabled={mode === 'edit'} />}

        <FormControl fullWidth size="small" disabled={saving}>
          <InputLabel id="tile-dialog-chart-type">{t('analytics.tile.tileTypeLabel')}</InputLabel>
          <Select
            labelId="tile-dialog-chart-type"
            label={t('analytics.tile.tileTypeLabel')}
            value={chartType}
            onChange={(e) => setChartType(e.target.value as DashboardChartType)}
          >
            {availableChartTypes.map((ct) => (
              <MenuItem key={ct} value={ct}>
                {t(CHART_TYPE_LABEL_KEYS[ct] ?? '')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isKpi && (
          <FormControl fullWidth size="small" disabled={saving}>
            <InputLabel id="tile-dialog-kpi-metric">{t('analytics.tile.metricLabel')}</InputLabel>
            <Select
              labelId="tile-dialog-kpi-metric"
              label={t('analytics.tile.metricLabel')}
              value={kpiMetric}
              onChange={(e) => setKpiMetric(e.target.value as TotalsMetricKey)}
            >
              {TOTALS_METRIC_ORDER.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(config.totalsMetrics[key].labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <ConfigPreviewGrid>
          {previewChips.map((item, idx) => (
            <ConfigChip key={item.labelKey} delay={50 * idx}>
              <ConfigChipLabel>{t(item.labelKey)}</ConfigChipLabel>
              <ConfigChipValue>{item.value}</ConfigChipValue>
            </ConfigChip>
          ))}
        </ConfigPreviewGrid>
      </DialogBody>

      <DialogFooter>
        <QcsButton variant="outlined" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </QcsButton>
        <QcsButton variant="contained" onClick={handleConfirm} disabled={saving}>
          {saving && <CircularProgress size={16} sx={{ mr: 0.75 }} color="inherit" />}
          {t(mode === 'create' ? 'analytics.tile.save' : 'analytics.tile.update')}
        </QcsButton>
      </DialogFooter>
    </Dialog>
  );
};
